import {chain, isUndefined, last} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {RendererOf} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {PaletteColorsEnum} from '../../../../../../../libs/shared/core/src/models/api/paletteModel';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {makeSizeConstants} from '../../../../../../../libs/shared/ui/src/styles/styleHelpers';
import {HighlightRangeModel} from '../../../../../../models/api/conversationHighlightModel';

// @TODO: Move to src/components/inboxes/cells/search (not in header any longer).

/*
 * Props.
 */

interface HighlightProps {
  text: string;
  ranges: ReadonlyArray<HighlightRangeModel>;
  highlightPartRenderer?: RendererOf<string>;
  backgroundColor?: PaletteColorsEnum;
  size?: VisualSizesEnum;
  isParentSelected?: boolean;
  hideWhitespaceOnly?: boolean;
  fontWeight?: keyof typeof fontWeights;
}

/*
 * Style.
 */

interface HighlightStyleProps {
  $backgroundColor?: PaletteColorsEnum;
  $size?: VisualSizesEnum;
  $fontWeight?: keyof typeof fontWeights;
}

export const StyledHighlightStrong = styled.strong<HighlightStyleProps>`
  ${(p) => maybeApplyBackgroundColor(p)}

  // If we don't have a visual highlight, change the font weight.
  font-weight: ${(p) => {
    if (p.$fontWeight) {
      return fontWeights[p.$fontWeight];
    }
    return p.$backgroundColor ? 'inherit' : fontWeights.semibold;
  }}
`;

const visualPaddingSizes = makeSizeConstants('0 1px 1px 1px', '0 2px 1px 2px');
const visualMarginSizes = makeSizeConstants('0 -1px -1px -1px', '0 1px -1px -2px');

function maybeApplyBackgroundColor(props: HighlightStyleProps) {
  const {$backgroundColor, $size = VisualSizesEnum.MEDIUM} = props;

  if ($backgroundColor) {
    return css`
      padding: ${visualPaddingSizes[$size]};
      margin: ${visualMarginSizes[$size]};

      border-radius: 4px;

      background-color: ${(p) => $backgroundColor && p.theme.palette[$backgroundColor].highlight};
      color: ${(p) => p.theme.highlightText};
    `;
  }

  return css``;
}

interface HighlightSpanStyleProps {
  $isParentSelected?: boolean;
}

// If the parent element is selected, we should use `invertedText` color for non-highlighted parts.
// Otherwise, just use the color from the parent element.
const StyledSpan = styled.span<HighlightSpanStyleProps>`
  color: ${(p) => (p.$isParentSelected ? p.theme.invertedText : 'inherit')};
`;

/*
 * Components.
 */

/** Transform string into ReactElement with highlighted results. */
export const Highlight: FC<HighlightProps> = ({
  text,
  ranges,
  highlightPartRenderer,
  backgroundColor,
  size,
  isParentSelected,
  hideWhitespaceOnly,
  fontWeight,
}) => {
  const highlightParts = splitByHighlightRanges(text, ranges);
  const highlightedResult = highlightParts.map((part, index) => {
    const renderedPart = highlightPartRenderer ? highlightPartRenderer(part.text) : part.text;

    if (part.isHighlighted) {
      return (
        <StyledHighlightStrong
          $backgroundColor={backgroundColor}
          $size={size}
          $fontWeight={fontWeight}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          {renderedPart}
        </StyledHighlightStrong>
      );
    }

    if (hideWhitespaceOnly && part.text.trim().length === 0) {
      return null;
    }

    return (
      <StyledSpan
        $isParentSelected={isParentSelected}
        // eslint-disable-next-line react/no-array-index-key
        key={index}
      >
        {renderedPart}
      </StyledSpan>
    );
  });

  return <>{highlightedResult}</>;
};

interface HighlightPart {
  text: string;
  isHighlighted: boolean;
}

function splitByHighlightRanges(
  text: string,
  ranges: ReadonlyArray<HighlightRangeModel>,
): ReadonlyArray<HighlightPart> {
  // If no highlight ranges provided, simply return input text.
  if (ranges.length === 0) {
    return [{text, isHighlighted: false}];
  }

  const mergedRanges = sortAndMergeHighlightRanges(ranges);

  // Otherwise, iterate over the ranges to highlight them.
  let lastPopulatedIndex = 0;
  const highlightedResult = [];
  for (const range of mergedRanges) {
    const highlightStart = range.start;
    const highlightEnd = highlightStart + range.length;

    // If ranges are incorrect, exit for loop and return unhighlighted text.
    if (isUndefined(text[highlightStart]) || isUndefined(text[highlightEnd - 1])) {
      return [{text, isHighlighted: false}];
    }

    // Maybe Render unhighlighted part.
    if (lastPopulatedIndex < highlightStart) {
      highlightedResult.push({text: text.slice(lastPopulatedIndex, highlightStart), isHighlighted: false});
    }

    // Render next highlighted part.
    highlightedResult.push({text: text.slice(highlightStart, highlightEnd), isHighlighted: true});

    // Update last populated index.
    lastPopulatedIndex = highlightEnd;
  }

  // Populate the end of the text.
  const remainingText = text.slice(lastPopulatedIndex, text.length);
  highlightedResult.push({text: remainingText, isHighlighted: false});

  return highlightedResult;
}

// TODO(ldemarne): Add tests.
export function truncateHighlightRanges(
  ranges: ReadonlyArray<HighlightRangeModel>,
  limit: number,
): ReadonlyArray<HighlightRangeModel> {
  return chain(ranges)
    .map((range) => {
      if (range.start >= limit) {
        return undefined;
      }

      return {...range, length: Math.min(range.length, limit - range.start)};
    })
    .compact()
    .value();
}

export function sortAndMergeHighlightRanges(ranges: ReadonlyArray<HighlightRangeModel>) {
  // Exit early if there's nothing to do.
  if (ranges.length < 2) {
    return ranges;
  }

  // Sort ranges by starting position, ascending.
  const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
  const mergedRanges: HighlightRangeModel[] = [];

  for (const range of sortedRanges) {
    const lastRange = last(mergedRanges);

    // This is the first range.
    if (!lastRange) {
      mergedRanges.push(range);
    }
    // This range doesn't overlap with the last range.
    else if (lastRange.start + lastRange.length < range.start) {
      // This range doesn't overlap with the last range.
      mergedRanges.push(range);
    }
    // This range overlaps with the last range.
    else {
      mergedRanges[mergedRanges.length - 1] = {
        start: lastRange.start,
        // Find rightmost boundary.
        length: Math.max(lastRange.start + lastRange.length, range.start + range.length) - lastRange.start,
      };
    }
  }

  return mergedRanges;
}
